<template>
	<div class="group-invites-container">
		<div class="group-invites-header" v-if="hasLinks">
			{{ $t('groupinvites_invite_prompt') }}
			<Button primary @click="showCreation = true">
				{{ $t('groupinvites_invite_create_label') }}
			</Button>
		</div>

		<Table
			:columns="columns"
			:rows="invites"
			clickable
			closePopoversOnScroll
			:error="fetchError"
			:hideHeader="!hasLinks"
			:loadingOverlay="isLoading"
			scrollable
			@row-click="openDetails"
			keyField="code"
			height="calc(100vh - 312px)"
			ref="table"
		>
			<template #cell-expires="{ value }">
				{{ formatExpDate(value) }}
			</template>
			<template #cell-access="{ value }">
				<div v-if="value === INTERNAL" class="internal-access">
					<GlobeSlashIcon type="regular" /> {{ $t('common_disabled') }}
				</div>
				<div v-if="value === EXTERNAL" class="external-access">
					<GlobeIcon type="regular" /> {{ $t('common_enabled') }}
				</div>
			</template>
			<template #cell-actions="{ data }">
				<DotsMenu
					:items="menuOptions"
					@click.stop
					@select="onMenuItemClick($event, data)"
				/>
			</template>
			<template #empty>
				<div class="empty-state">
					<LinkIcon backdrop />
					<h2 v-t="'groupinvites_invite_empty'" />
					<p v-t="'groupinvites_invite_prompt'" />
					<Button primary @click="showCreation = true">
						{{ $t('groupinvites_invite_create_label') }}
					</Button>
				</div>
			</template>
		</Table>

		<!-- DETAILS MODAL -->
		<StandardModal
			v-if="inviteDetails"
			open
			small
			:title="this.$t('groupinvites_title_singular')"
			hideConfirm
			:cancelText="$t('common_button_done')"
			@cancel="inviteDetails = null"
			class="details-modal"
		>
			<div class="details-content">
				<Input v-model="inviteDetails.url" readonly class="copy-input">
					<template #after>
						<CopyToClipboard
							class="copy-btn"
							:value="inviteDetails.url"
							:tippyProps="{ offset: [0, 10] }"
						>
							<CopyIcon type="regular" />
						</CopyToClipboard>
						<Tippy
							content="QR Code"
							class="qr-code-btn"
							:options="{ offset: [0, 0] }"
							@click="onClickQRCode"
						>
							<QRCodeIcon type="regular" />
						</Tippy>
					</template>
				</Input>

				<div class="data-list">
					<div class="data-list-item">
						<div class="data-list-item-left">
							<LinkIcon type="regular" />
							{{ $t('groupinvites_invite_code_label') }}
						</div>
						<div class="data-list-item-right">
							{{ inviteDetails.code }}
						</div>
					</div>
					<div class="data-list-item">
						<div class="data-list-item-left">
							<CalendarClockIcon type="regular" />
							{{ $t('groupinvites_expire_date') }}
						</div>
						<div class="data-list-item-right">
							{{ formatExpDate(inviteDetails.expires) }}
						</div>
					</div>
					<div class="data-list-item">
						<div class="data-list-item-left">
							<PenToSquareIcon type="regular" />
							{{ $t('groupinvites_invite_creator_label') }}
						</div>
						<div class="data-list-item-right">
							{{ inviteDetails.user.fullname }}
							<div
								class="created-date"
								v-text="$m(inviteDetails.created_at).format('l')"
							/>
						</div>
					</div>
					<div class="data-list-item">
						<div
							class="data-list-item-left"
							:class="{ [inviteDetails.access]: true }"
						>
							<GlobeIcon
								v-if="inviteDetails.access === EXTERNAL"
								type="regular"
							/>
							<GlobeSlashIcon
								v-if="inviteDetails.access === INTERNAL"
								type="regular"
							/>
							{{ $t('groupinvites_invite_acct_creation_title') }}
						</div>
						<div
							class="data-list-item-right"
							v-text="
								$t(
									inviteDetails.access === EXTERNAL
										? 'common_enabled'
										: 'common_disabled'
								)
							"
						/>
					</div>
				</div>
				<div class="access-description">
					{{
						$t(
							inviteDetails.access === EXTERNAL
								? 'groupinvites_invite_acct_creation_desc_enabled'
								: 'groupinvites_invite_acct_creation_desc_disabled'
						)
					}}
				</div>
			</div>

			<template #footer-left>
				<Button danger @click="inviteToDelete = inviteDetails">
					{{ $t('common_button_delete') }}
				</Button>
			</template>
		</StandardModal>

		<!-- DELETE MODAL -->
		<StandardModal
			v-if="inviteToDelete"
			open
			small
			:title="$t('groupinvites_invite_delete_label')"
			:confirmText="$t('common_button_delete')"
			class="delete-modal"
			:confirmProps="{ danger: true, loading: isDeleting }"
			@cancel="inviteToDelete = null"
			@confirm="deleteInvite(inviteToDelete)"
			ref="deleteModal"
		>
			<div class="delete-invite">
				<TrashCanIcon halo />
				<div class="help" v-t="'groupinvites_invite_delete_desc'" />
			</div>
		</StandardModal>

		<!-- QR CODE MODAL -->
		<ImageModal
			v-if="qrCodeUrl"
			:imageUrl="qrCodeUrl"
			:downloadName="`invite-${inviteDetails.code}`"
			@close="qrCodeUrl = null"
		/>

		<GroupInviteCreation
			v-if="showCreation"
			@success="addToInvites"
			@cancel="showCreation = false"
			:group="group"
		/>
	</div>
</template>

<script>
import {
	Table,
	DotsMenu,
	Button,
	Input,
	StandardModal,
	CopyToClipboard,
	Tippy,
	ImageModal,
} from '@/components/common'
import {
	CopyIcon,
	LinkIcon,
	GlobeIcon,
	GlobeSlashIcon,
	CalendarClockIcon,
	PenToSquareIcon,
	TrashCanIcon,
	QRCodeIcon,
} from '@/icons'
import GroupInviteCreation, { accessTypes } from './GroupInviteCreation'
import {
	getOrganization,
	getCurrentChat,
	getMyMembershipForBubble,
} from '@/store'
import { addEventBusEvent, generateQRCodeUrl } from '@/helpers/utils'
import { get, del } from '@/helpers/api'
import { chatCan, ADD_MEMBER } from '@/helpers/permissions'

export default {
	name: 'GroupInvites',

	components: {
		Table,
		DotsMenu,
		LinkIcon,
		Button,
		Input,
		GlobeIcon,
		GlobeSlashIcon,
		StandardModal,
		CalendarClockIcon,
		PenToSquareIcon,
		CopyToClipboard,
		CopyIcon,
		TrashCanIcon,
		GroupInviteCreation,
		QRCodeIcon,
		Tippy,
		ImageModal,
	},

	props: {
		bubbleId: [Number, String],
		showNew: { type: Boolean, default: null },
	},

	data() {
		return {
			inviteDetails: null,
			inviteToDelete: null,
			isLoading: true,
			fetchError: false,
			isDeleting: false,
			showCreation: false,
			qrCodeUrl: null,
			invites: [],
			group: getCurrentChat(),
			org: getOrganization(),
			...accessTypes,
			columns: [
				{
					field: 'code',
					label: this.$t('groupinvites_invite_code_label'),
					width: '300px',
				},
				{
					field: 'expires',
					label: this.$t('groupinvites_expire_date'),
					width: '400px',
				},
				{
					field: 'access',
					label: this.$t('groupinvites_invite_acct_creation_title'),
				},
				{
					field: 'actions',
					align: 'right',
					width: '100px',
				},
			],
			menuOptions: [
				{
					id: 'details',
					label: this.$t('groupinvites_invite_view_details'),
				},
				{
					id: 'delete',
					label: this.$t('common_button_delete'),
					danger: true,
				},
			],
		}
	},

	mounted() {
		this.checkQueryParams()
		this.checkForMembership()
		if (this.isLoading) {
			this.removeEvent = addEventBusEvent('membership-update', () => {
				this.checkForMembership()
			})
		}
		if (this.showNew) {
			this.showCreation = true
		}
	},

	beforeUnmount() {
		this.removeEvent?.()
	},

	computed: {
		hasLinks() {
			return this.invites.length > 0
		},
	},

	methods: {
		addToInvites(data) {
			this.inviteDetails = data
			this.invites.push(data)
		},

		async fetchInvites() {
			try {
				this.fetchError = false
				this.isLoading = true
				const { data } = await get(
					`/api/clients/groups/${this.bubbleId}/invites`
				)
				this.invites = data.data
			} catch (e) {
				console.error(e)
				this.fetchError = true
			}
			this.isLoading = false
		},

		formatExpDate(date) {
			if (!date) return this.$t('groupinvites_expire_option_none') // never
			const now = this.$m()
			const m = this.$m(date)
			let dateFormat = 'l' // some future date
			if (m.isSame(now, 'day')) {
				dateFormat = `[${this.$t('common_today')}]`
			} else if (m.isSame(now.add(1, 'days'), 'day')) {
				dateFormat = `[${this.$t('common_tomorrow')}]`
			}
			return m.format(`${dateFormat}, LT`)
		},

		async deleteInvite(invite) {
			this.deleteError?.()
			this.isDeleting = true
			try {
				await del(`/api/clients/invites/${invite.code}`)
				this.inviteDetails = null
				this.inviteToDelete = null
				this.$alert.success(this.$t('groupinvites_invite_delete_success'))
				this.invites = this.invites.filter((i) => i.code !== invite.code)
			} catch (e) {
				console.error(e)
				this.deleteError = this.$refs.deleteModal.$alert.error()
			}
			this.isDeleting = false
		},

		openDetails(invite) {
			this.inviteDetails = invite
		},

		onMenuItemClick({ id }, invite) {
			switch (id) {
				case 'details':
					return this.openDetails(invite)
				case 'delete':
					return (this.inviteToDelete = invite)
			}
		},

		checkQueryParams() {
			const { new: showNew, ...query } = this.$route.query
			if (showNew) {
				this.$router.replace({ name: 'groupinvites', query })
			}
		},

		checkForMembership() {
			const membership = getMyMembershipForBubble(this.bubbleId)
			if (this.isLoading && this.group && membership) {
				const canAddMembers = chatCan(getCurrentChat(), ADD_MEMBER)
				if (!canAddMembers) {
					this.$router.replace({
						name: 'groupoptions',
						params: this.$route.params,
					})
					return
				}
				this.fetchInvites()
			}
		},

		onClickQRCode() {
			this.qrCodeUrl = generateQRCodeUrl(this.inviteDetails.url)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.group-invites-header {
	@include flex-spread;
	margin-bottom: 16px;
	color: var(--neutral-400);
}

.external-access,
.internal-access {
	@include flex(8px);
	:deep(.pronto-icon) {
		width: 1.5em;
		font-size: 18px;
	}
}
.external-access {
	padding-left: 2px;
}

.empty-state {
	text-align: center;
	.pronto-icon {
		font-size: 100px;
		color: var(--teal-300);
	}
}

.copy-input {
	:deep(.after) {
		color: var(--neutral-600);
		font-size: 19px;
		.copy-container {
			@include flex-center;
		}
		.pronto-icon {
			display: block;
		}
	}
}
.copy-btn {
	@include flex-center;
	padding-right: 12px;
	&:hover {
		color: var(--teal-300);
	}
	.pronto-icon {
		display: block;
	}
}

.qr-code-btn {
	@include flex-center;
	padding-left: 12px;
	height: 40px;
	border-left: 1px solid var(--neutral-200);
	cursor: pointer;
	&:hover {
		color: var(--teal-300);
	}
}

.details-content {
	padding: 12px;
	.pronto-input {
		margin-bottom: 12px;
	}
	.created-date {
		font-size: 12px;
	}
	:deep(.pronto-input) {
		.after {
			height: 40px;
			padding: 0 14px;
			border-left: 1px solid var(--neutral-200);
		}
	}
	:deep(.data-list-item-left.internal) {
		.pronto-icon {
			margin-right: 6px !important;
		}
	}
}

.access-description {
	font-size: 14px;
	color: var(--neutral-500);
	padding: 0 8px;
	margin-top: 8px;
}

.delete-invite {
	padding: 24px 60px;
	@include flex-column-center;
	text-align: center;
	.pronto-icon {
		color: var(--red-200);
		font-size: 45px;
		margin-bottom: 32px;
	}
}

:deep(.table-header) {
	border-top: none !important;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.details-modal {
	.modal-content {
		height: 472px !important;
	}

	.data-list {
		border: 1px solid var(--neutral-200);
		border-radius: 6px;
		overflow: hidden;
		background: #fafafa;
		.data-list-item {
			@include flex-spread;
			padding: 10px 16px;
			border-bottom: 1px solid var(--neutral-200);
			&:last-child {
				border-bottom: 0;
			}
			.data-list-item-left {
				@include flex;
				font-size: 15px;
				.pronto-icon {
					font-size: 20px;
					margin-right: 16px;
				}
			}
			.data-list-item-right {
				font-size: 15px;
				text-align: right;
				color: var(--neutral-400);
			}
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.details-modal {
		.data-list {
			background: var(--neutral-100);
		}
	}
	.group-invites-container {
		.empty-state {
			text-align: center;
			.pronto-icon-backdrop {
				.backdrop {
					color: var(--neutral-100);
				}
			}
		}
	}
}
</style>
